import { useEffect, useMemo, useState } from 'react';
import { Board } from '../board/Board';
import { MoveList } from './MoveList';
import type { Color, ParsedGame } from '../../types';

export interface GameViewerProps {
  game: ParsedGame;
  playerColor: Color;
  highlightPlies?: Set<number>;
  /** Contrôle externe optionnel (utilisé par le panneau d'analyse pour synchroniser le ply affiché). */
  currentPly?: number;
  onPlyChange?: (ply: number) => void;
}

function startFen(game: ParsedGame): string {
  if (game.moves.length > 0) return game.moves[0].fenBefore;
  return game.headers.FEN ?? 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
}

export function GameViewer({ game, playerColor, highlightPlies, currentPly: controlledPly, onPlyChange }: GameViewerProps) {
  const fens = useMemo(() => [startFen(game), ...game.moves.map((m) => m.fenAfter)], [game]);
  const [internalPly, setInternalPly] = useState(0);
  const [orientation, setOrientation] = useState<Color>(playerColor);

  const currentPly = controlledPly ?? internalPly;
  const setPly = (ply: number) => {
    const clamped = Math.max(0, Math.min(fens.length - 1, ply));
    if (onPlyChange) onPlyChange(clamped);
    else setInternalPly(clamped);
  };

  useEffect(() => setOrientation(playerColor), [playerColor]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setPly(currentPly - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setPly(currentPly + 1);
      } else if (e.key === 'Home') {
        setPly(0);
      } else if (e.key === 'End') {
        setPly(fens.length - 1);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPly, fens.length]);

  const currentFen = fens[currentPly];
  const lastMove = currentPly > 0 ? game.moves[currentPly - 1] : null;
  const sideToMoveLabel = currentFen.split(' ')[1] === 'w' ? 'Trait aux Blancs' : 'Trait aux Noirs';

  return (
    <div className="game-viewer">
      <div className="board-column">
        <Board
          fen={currentFen}
          orientation={orientation}
          lastMoveSquares={lastMove ? { from: lastMove.uci.slice(0, 2), to: lastMove.uci.slice(2, 4) } : null}
          boardId="main-board"
        />
        <div className="board-status">
          <span>{sideToMoveLabel}</span>
          <span className="muted">
            {lastMove ? `Dernier coup : ${lastMove.san}` : 'Position de départ'}
          </span>
        </div>
        <div className="nav-controls" role="group" aria-label="Navigation dans la partie">
          <button type="button" onClick={() => setPly(0)} disabled={currentPly === 0} aria-label="Début">
            ⏮
          </button>
          <button type="button" onClick={() => setPly(currentPly - 1)} disabled={currentPly === 0} aria-label="Coup précédent">
            ◀
          </button>
          <button
            type="button"
            onClick={() => setPly(currentPly + 1)}
            disabled={currentPly === fens.length - 1}
            aria-label="Coup suivant"
          >
            ▶
          </button>
          <button
            type="button"
            onClick={() => setPly(fens.length - 1)}
            disabled={currentPly === fens.length - 1}
            aria-label="Fin"
          >
            ⏭
          </button>
          <button type="button" onClick={() => setOrientation((o) => (o === 'w' ? 'b' : 'w'))} aria-label="Retourner l'échiquier">
            ⟲ Retourner
          </button>
        </div>
      </div>
      <div className="movelist-column">
        <MoveList moves={game.moves} currentPly={currentPly} onJump={setPly} highlightPlies={highlightPlies} />
      </div>
    </div>
  );
}
